import { useState, useMemo } from 'react';
import { X, ArrowUp, Loader2, Users } from 'lucide-react';
import { SongRequest } from '../../services/requests';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

interface LimitRaiseModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  currentLimit: number;
  overflowSongs: SongRequest[];
}

function normalizeKey(title: string, artist: string): string {
  return (title + '|' + artist).toLowerCase().trim();
}

export default function LimitRaiseModal({ isOpen, onClose, eventId, currentLimit, overflowSongs }: LimitRaiseModalProps) {
  const [releasing, setReleasing] = useState(false);

  const predictions = useMemo(() => {
    // Group overflow songs by attendee
    const byAttendee = new Map<string, SongRequest[]>();
    for (const song of overflowSongs) {
      const aid = song.requestedBy.oderId;
      if (!byAttendee.has(aid)) byAttendee.set(aid, []);
      byAttendee.get(aid)!.push(song);
    }

    // Sort each attendee's songs by priority
    for (const [, songs] of byAttendee) {
      songs.sort((a, b) => (a.overflowPriority ?? 999) - (b.overflowPriority ?? 999));
    }

    const results: Array<{ increment: number; uniqueSongs: number; totalReleases: number; duplicates: number }> = [];

    for (let n = 1; n <= 10; n++) {
      const seen = new Set<string>();
      let totalReleases = 0;
      let duplicates = 0;

      for (const [, songs] of byAttendee) {
        const toRelease = songs.slice(0, n);
        for (const song of toRelease) {
          totalReleases++;
          const key = normalizeKey(song.song.title, song.song.artist);
          if (seen.has(key)) {
            duplicates++;
          } else {
            seen.add(key);
          }
        }
      }

      results.push({
        increment: n,
        uniqueSongs: seen.size,
        totalReleases,
        duplicates,
      });

      // Stop if we've exhausted all overflow songs
      if (totalReleases >= overflowSongs.length) break;
    }

    return results;
  }, [overflowSongs]);

  const attendeeCount = useMemo(() => {
    return new Set(overflowSongs.map(s => s.requestedBy.oderId)).size;
  }, [overflowSongs]);

  const handleRelease = async (increaseBy: number) => {
    if (!confirm(`Raise the request limit by ${increaseBy} (${currentLimit} → ${currentLimit + increaseBy})? This will release overflow songs into the queue.`)) {
      return;
    }

    setReleasing(true);
    try {
      const functions = getFunctions();
      const release = httpsCallable<
        { eventId: string; increaseBy: number },
        { released: number; duplicatesMerged: number; newLimit: number }
      >(functions, 'releaseOverflowSongs');
      const result = await release({ eventId, increaseBy });
      const data = result.data;
      toast.success(
        `Limit raised to ${data.newLimit}! ${data.released} song${data.released !== 1 ? 's' : ''} released to queue.` +
        (data.duplicatesMerged > 0 ? ` (${data.duplicatesMerged} duplicate${data.duplicatesMerged !== 1 ? 's' : ''} merged)` : ''),
        { duration: 6000 }
      );
      onClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to raise limit: ${msg}`);
    } finally {
      setReleasing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Raise Request Limit</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Current: {currentLimit} per attendee &middot; {overflowSongs.length} overflow song{overflowSongs.length !== 1 ? 's' : ''} from {attendeeCount} attendee{attendeeCount !== 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Predictions */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Select how much to raise the limit. Each attendee&apos;s top-priority overflow songs will be released to the queue.
          </p>
          <div className="space-y-2">
            {predictions.map((pred) => (
              <button
                key={pred.increment}
                onClick={() => handleRelease(pred.increment)}
                disabled={releasing}
                className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-primary/5 dark:hover:border-primary dark:hover:bg-primary/10 transition-colors disabled:opacity-50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ArrowUp className="w-4 h-4 text-primary" />
                    <span className="font-medium text-gray-900 dark:text-white">
                      +{pred.increment} &rarr; {currentLimit + pred.increment}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-primary">
                    +{pred.uniqueSongs} song{pred.uniqueSongs !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="mt-1 text-xs text-gray-500 flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {pred.totalReleases} total release{pred.totalReleases !== 1 ? 's' : ''}
                  </span>
                  {pred.duplicates > 0 && (
                    <span className="text-amber-600 dark:text-amber-400">
                      {pred.duplicates} duplicate{pred.duplicates !== 1 ? 's' : ''} merged
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        {releasing && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Releasing overflow songs...
          </div>
        )}
      </div>
    </div>
  );
}
